/**
 * Environment Configuration
 *
 * Centralized access to all environment variables with type safety.
 * All VITE_ prefixed environment variables are public and safe to expose.
 */

/**
 * Validated environment configuration
 */
export const env = {
    // API Configuration
    API_URL: import.meta.env.VITE_API_URL || '/api',

    // Stripe (Public key only - never expose secret key!)
    STRIPE_PUBLISHABLE_KEY: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '',

    // Google Analytics
    GA_MEASUREMENT_ID: import.meta.env.VITE_GA_MEASUREMENT_ID || '',

    // Feature Flags
    FEATURE_REVIEWS: import.meta.env.VITE_FEATURE_REVIEWS === 'true',
    FEATURE_WISHLIST: import.meta.env.VITE_FEATURE_WISHLIST === 'true',

    // App Configuration
    APP_NAME: import.meta.env.VITE_APP_NAME || 'Stork Rentals',
    SUPPORT_EMAIL: import.meta.env.VITE_SUPPORT_EMAIL || 'info@thestorkexchange.ie',

    // SEO / Site
    // Canonical public origin (no trailing slash). Used for canonical tags,
    // Open Graph URLs, sitemap.xml and robots.txt.
    SITE_URL: (import.meta.env.VITE_SITE_URL || 'https://tserentals.com').replace(/\/+$/, ''),
    // When false (staging/preview), pages emit noindex and robots.txt blocks crawlers.
    SITE_INDEXABLE: import.meta.env.VITE_SITE_INDEXABLE !== 'false',

    // Preview gate ("Coming Soon" page) — off unless explicitly enabled.
    PREVIEW_GATE: import.meta.env.VITE_PREVIEW_GATE === 'true',
    PREVIEW_PASSWORD: import.meta.env.VITE_PREVIEW_PASSWORD || 'stork2026',

    // Staging/Preview mode - allows test Stripe keys in production builds
    // Set to 'false' to require pk_live_ keys (for real production)
    // Defaults to true (test keys allowed) until explicitly disabled
    ALLOW_TEST_STRIPE: import.meta.env.VITE_ALLOW_TEST_STRIPE !== 'false',

    // Build-time constants
    APP_VERSION: __APP_VERSION__,
    BUILD_TIME: __BUILD_TIME__,
    IS_PROD: __IS_PROD__,
    IS_DEV: import.meta.env.DEV,
    MODE: import.meta.env.MODE,
} as const;

/**
 * Validate required environment variables in production
 * Call this early in app initialization
 */
export function validateEnv(): void {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Required in production
    if (env.IS_PROD) {
        if (!env.API_URL || env.API_URL === '/api') {
            errors.push('VITE_API_URL is required in production');
        }

        if (!env.STRIPE_PUBLISHABLE_KEY) {
            errors.push('VITE_STRIPE_PUBLISHABLE_KEY is required in production');
        }

        // Verify Stripe key is publishable (pk_) not secret (sk_)
        if (env.STRIPE_PUBLISHABLE_KEY) {
            if (!env.STRIPE_PUBLISHABLE_KEY.startsWith('pk_')) {
                errors.push(
                    'VITE_STRIPE_PUBLISHABLE_KEY must be a publishable key (starts with pk_)'
                );
            }
            // Warn if using test key in production (unless explicitly allowed for staging)
            if (env.STRIPE_PUBLISHABLE_KEY.startsWith('pk_test_')) {
                if (env.ALLOW_TEST_STRIPE) {
                    warnings.push(
                        'Using TEST Stripe key in production build (VITE_ALLOW_TEST_STRIPE=true). Payments will be in TEST mode!'
                    );
                } else {
                    errors.push(
                        'Production must use pk_live_ Stripe key, not pk_test_. Set VITE_ALLOW_TEST_STRIPE=true for staging environments.'
                    );
                }
            }
        }

        // Optional but recommended - warn if missing
        if (!env.GA_MEASUREMENT_ID) {
            warnings.push('VITE_GA_MEASUREMENT_ID not configured - analytics disabled');
        }
    }

    // Log warnings
    if (warnings.length > 0) {
        warnings.forEach(w => console.warn(`[ENV] ${w}`));
    }

    // Handle errors
    if (errors.length > 0) {
        console.error('Environment validation errors:', errors);
        if (env.IS_PROD) {
            throw new Error(`Invalid environment configuration:\n${errors.join('\n')}`);
        }
    }
}

/**
 * Get a feature flag value with fallback
 */
export function isFeatureEnabled(feature: 'reviews' | 'wishlist'): boolean {
    switch (feature) {
        case 'reviews':
            return env.FEATURE_REVIEWS;
        case 'wishlist':
            return env.FEATURE_WISHLIST;
        default:
            return false;
    }
}

// Declare compile-time constants
declare const __APP_VERSION__: string;
declare const __BUILD_TIME__: string;
declare const __IS_PROD__: boolean;
